// RENDERING WHAT THE SERVER SAID — and marking it, so a test can tell the difference.
//
// Every owner-facing sentence that states a fact or a decision arrives as `Copy { code, text }` and is
// laid out BY CODE (R226). These components are the only way such a sentence
// reaches the page, and each one stamps `data-copy-code` on the element it renders. That attribute is
// what `test/serverSentences.test.tsx` asserts on: a sentence with no code is a sentence this client
// composed, which is the defect the whole design is arranged to prevent.
//
// NOTHING HERE USES `dangerouslySetInnerHTML`. The prototype built every screen by assigning template
// strings to `#root.innerHTML` behind an `esc()` that did not escape `'` (inventory §4 "Worse" #15).
// React escapes text nodes by construction, and this package has no escaping helper of its own because
// it has nothing to escape.

import type { ReactNode } from "react";
import type { Copy, DecisionNotRun } from "@contract";
import type { AnyDecision } from "../transport/types";
import { fieldWords, heldTemplate, heldText, type AnyHeldId } from "./heldCopy";

/** One server sentence. `code` is on the element; the text is a React text node, never markup. */
export function CopyLine({ copy, className }: { copy: Copy; className?: string }) {
  return (
    <p className={className === undefined ? "copy" : `copy ${className}`} data-copy-code={copy.code}>
      {copy.text}
    </p>
  );
}

/** A server sentence inside a bordered note. `tone` is chrome; the words are the server's. */
export function CopyNote({ copy, tone }: { copy: Copy; tone: "warn" | "good" | "stop" }) {
  return (
    <div className={`note ${tone}`} data-copy-code={copy.code} role={tone === "stop" ? "alert" : undefined}>
      {copy.text}
    </div>
  );
}

/**
 * A decision that never ran, shown as such.
 *
 * EMPTY MUST NEVER READ AS CLEAN (CLAUDE.md, the guard rules). The prototype had no way to say this at
 * all — a fixture either had a value or the screen rendered nothing — so an absent answer and a
 * favourable one looked identical. Here they cannot.
 *
 * IN THE OWNER'S WORDS (WEB-PARITY-1B, SW-5). It used to print why the decision had not run, the raw field names it was
 * waiting on and the endpoint whose job it was ("It is v2Value's to answer."). Now it names the check (`what`) and says
 * W-2, the sentence Tyler approved for exactly this ("2. approve", `heldCopy.ts`). Why it did not run, and what it is
 * waiting on, stay on the element for us: `data-not-run`, `data-missing`, `data-computed-by`.
 *
 * ONLY FOR A CHECK THAT CAN STILL RUN (WEB-PARITY-1C, F-WP1B-5). W-2 says "Try again in a moment", which is true of a
 * check that is missing an input, not computed yet, or failed — and false of one that does not apply to this appeal
 * (`notApplicable`: NEW-1 on a house, a window rule outside its state). That one shows nothing, as the app shows
 * nothing, and it is not an empty that could read as clean: the server has said it does not apply.
 */
export function NotRunNote({ decision, what }: { decision: { ran: false } & DecisionNotRun; what: string }) {
  if (decision.why === "notApplicable") return null;
  return (
    <div
      className="notrun"
      data-not-run={decision.why}
      data-missing={decision.missing !== undefined && decision.missing.length > 0 ? decision.missing.join(" ") : undefined}
      data-computed-by={decision.computedBy}
    >
      <b>{what}</b> <span data-held-copy="check.notRunYet">{heldText("check.notRunYet")}</span>
    </div>
  );
}

/**
 * The general renderer for one decision: its sentence when it has one, a "never ran" note when it did
 * not run, and nothing at all when it passed silently — which is what a `pass` with `copy: null` means.
 */
export function DecisionView({
  decision,
  what,
  tone = "warn",
}: {
  decision: AnyDecision;
  what: string;
  tone?: "warn" | "good" | "stop";
}) {
  if (!decision.ran) return <NotRunNote decision={decision} what={what} />;
  if (decision.copy === null) return null;
  const chosen = decision.outcome === "block" ? "stop" : decision.outcome === "info" ? "good" : tone;
  return <CopyNote copy={decision.copy} tone={chosen} />;
}

/**
 * A SENTENCE THIS CLIENT HOLDS ITSELF, as Tyler approved it (WEB-COPY-1 or WEB-BENEFITS; `heldCopy.ts` says which and
 * why).
 *
 * It is stamped `data-held-copy`, never `data-copy-code`: the two must stay distinguishable on the page, because a held
 * sentence standing where a server sentence belongs is the defect `data-copy-code` exists to catch.
 */
export function HeldLine({ id, className }: { id: AnyHeldId; className?: string }) {
  return (
    <p className={className === undefined ? "copy" : `copy ${className}`} data-held-copy={id}>
      {heldText(id)}
    </p>
  );
}

/**
 * A SENTENCE THIS CLIENT CANNOT SOURCE.
 *
 * The rule is that the server owns every sentence stating a fact or a decision, and the prototype's
 * copy carries forward only where `docs/WEB_PATHWAY_INVENTORY.md` §5 marks the claim TRUE. Where
 * neither applies, the screen says so in the open rather than inventing a sentence — an invented one
 * would be indistinguishable from a real one the moment it shipped.
 *
 * NONE IS RENDERED TODAY. WEB-PAY-2 listed twenty-two in `docs/WEB_COPY_TODO.md`, and WEB-COPY-1 replaced every one
 * with the wording Tyler approved on 2026-09-29; that file is now the record of what was approved and where it went.
 * The component stays for the next sentence nobody has written yet; `web/test/copyTodoList.test.ts` asserts that none
 * is rendered, so a new placeholder is a deliberate edit to that test and to the record, never a quiet addition.
 */
export function TodoCopy({ id, need }: { id: string; need: string }) {
  return (
    <div className="todo-copy" data-todo-copy={id}>
      <code>TODO(copy: {id})</code> — {need}
    </div>
  );
}

/**
 * What the server could not use from what the owner sent, in plain words (WEB-PARITY-1B, SW-6).
 *
 * It used to print each raw `field — problem` line ("ownerComps[].address — empty"). Now it is W-3, the sentence Tyler
 * approved for exactly this ("2. approve", `heldCopy.ts`), naming each field by the app's own label for it
 * (`FIELD_WORDS`). An entry with no label there is not something the owner entered — the engine's own gap, such as a
 * condition deduction with no sales to anchor it — and the app says nothing about those, so this says nothing either.
 * Every entry, named or not, stays on the element for us: `data-degraded-count` and `data-degraded-fields`.
 */
export function DegradedList({ degraded }: { degraded: readonly { field: string; problem: string }[] }) {
  if (degraded.length === 0) return null;
  const words = [...new Set(degraded.map((d) => fieldWords(d.field)).filter((w): w is string => w !== null))];
  if (words.length === 0) return null;
  return (
    <div
      className="degraded"
      data-degraded-count={degraded.length}
      data-degraded-fields={degraded.map((d) => d.field).join(" ")}
      data-held-copy="input.partlyUnused"
    >
      {heldTemplate("input.partlyUnused", { field: words.join(", ") })}
    </div>
  );
}

/**
 * A served sentence whose `**word**` is bold, as the app's Markdown-rendering `Text` shows it (WEB-PARITY-1B, YU-3):
 * the served can-raise attestation bolds one word that way (AssentCopy.swift:29), and `/you` printed its asterisks. Only
 * `**…**` is read; every run is a React text node, so nothing in the sentence is ever markup.
 */
export function WithBold({ text }: { text: string }) {
  const runs = text.split("**");
  // An unpaired `**` is not emphasis: the sentence is shown as it arrived.
  if (runs.length % 2 === 0) return <>{text}</>;
  return <>{runs.map((run, i) => (i % 2 === 1 ? <strong key={i}>{run}</strong> : run))}</>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="eyebrow">{children}</p>;
}
